import { useState } from "react";
import { useProductSubgroups, ProductSubgroupFormData, ProductSubgroup } from "@/features/produtos/hooks/useProductSubgroups";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Ban } from "lucide-react";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";

export default function ProductSubgroups() {
  const { subgroups, isLoading, createSubgroup, updateSubgroup, deleteSubgroup } = useProductSubgroups();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSubgroup, setEditingSubgroup] = useState<ProductSubgroup | null>(null);
  const [inativarId, setInativarId] = useState<number | null>(null);
  const [formData, setFormData] = useState<ProductSubgroupFormData>({
    descricao: "",
    referencia: "",
    isInativo: false,
  });
  const [busca, setBusca] = useState("");

  const subgroupsFiltrados = busca
    ? subgroups.filter(s =>
        s.descricao?.toLowerCase().includes(busca.toLowerCase()) ||
        (s.referencia ?? "").toLowerCase().includes(busca.toLowerCase()))
    : subgroups;

  const paginacao = usePaginacaoLocal(subgroupsFiltrados);

  const handleOpenDialog = (subgroup?: ProductSubgroup) => {
    if (subgroup) {
      setEditingSubgroup(subgroup);
      setFormData({
        descricao: subgroup.descricao || "",
        referencia: subgroup.referencia || "",
        isInativo: subgroup.isInativo,
      });
    } else {
      setEditingSubgroup(null);
      setFormData({ descricao: "", referencia: "", isInativo: false });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingSubgroup) {
      await updateSubgroup.mutateAsync({ id: editingSubgroup.id, ...formData });
    } else {
      await createSubgroup.mutateAsync(formData);
    }
    setDialogOpen(false);
  };

  if (isLoading) return <div className="p-6">Carregando...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Subgrupos de Produtos</h1>
          <p className="text-muted-foreground">Gerencie os subgrupos de produtos</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Subgrupo
        </Button>
      </div>

      <div className="max-w-sm">
        <Input
          placeholder="Buscar por descrição ou referência..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
        />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Descrição</TableHead>
              <TableHead>Referência</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginacao.itensPaginados.map((subgroup) => (
              <TableRow key={subgroup.id}>
                <TableCell className="font-medium">{subgroup.descricao || "-"}</TableCell>
                <TableCell>{subgroup.referencia || "-"}</TableCell>
                <TableCell className="text-center">
                  <Badge variant={subgroup.isInativo ? "secondary" : "success"}>
                    {subgroup.isInativo ? "Inativo" : "Ativo"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(subgroup)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {!subgroup.isInativo && (
                      <Button variant="ghost" size="icon" onClick={() => setInativarId(subgroup.id)}>
                        <Ban className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {paginacao.itensPaginados.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  {busca ? "Nenhum subgrupo encontrado para esta busca." : "Nenhum subgrupo cadastrado"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSubgroup ? "Editar Subgrupo" : "Novo Subgrupo"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição *</Label>
              <Input
                id="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="referencia">Referência</Label>
              <Input
                id="referencia"
                value={formData.referencia}
                onChange={(e) => setFormData({ ...formData, referencia: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="ativo"
                checked={!formData.isInativo}
                onCheckedChange={(v) => setFormData({ ...formData, isInativo: !v })}
              />
              <Label htmlFor="ativo">Ativo</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!inativarId} onOpenChange={() => setInativarId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar subgrupo</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza que deseja inativar este subgrupo de produto?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (inativarId) { deleteSubgroup.mutate(inativarId); setInativarId(null); } }}>
              Inativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
